import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SearchInput } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import { Table, TableBody, TableDisplaySkeletonRows, TablePagination } from '@/enterprise/components/Table';
import { PlayableInvoiceFetchOptions } from '@/enterprise/data/usePlayableInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useDataDog from '@/reporting/DataDog';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { useCursorPagination } from '@/utils/useCursorPagination';
import useUrlState from '@/utils/useUrlState';
import NoDataState from '../../NoDataState';
import { useInvoiceCentralLayoutContext } from '../InvoiceCentralLayout';
import EligibleInvoiceFilters, { EligibleInvoiceFilterOptions } from './EligibleInvoiceFilters';
import { EligibleInvoicesTable } from './EligibleInvoicesTable';
import { mergeFilters } from './utils';

type EligibleInvoiceSortKey = Extract<
  NonNullable<PlayableInvoiceFetchOptions>['sortBy'],
  'voucherId' | 'amount' | 'originalDepositDate' | 'daysPaidEarly'
>;
export interface EligibleInvoiceUrlState {
  pageSize: number;
  sort: {
    key: EligibleInvoiceSortKey;
    direction: 'asc' | 'desc';
  };
  searchValue?: string;
  filters?: EligibleInvoiceFilterOptions;
}

// Inner component that owns cursor pagination state
// Remounts when buyer/currency changes, preventing stale cursor from being used in API calls
const EligibleInvoicesContent = ({
  makerOrganizationUuid,
  currency,
  takerMarkets,
}: {
  makerOrganizationUuid: string;
  currency: string;
  takerMarkets: TakerMarket[];
}) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const { currentCursor, currentPage, canGoBack, goToNextPage, goToPreviousPage, reset } = useCursorPagination();
  const { addAction } = useDataDog();

  const [urlState, setUrlState] = useUrlState<EligibleInvoiceUrlState>({
    pageSize: 25,
    sort: { key: 'amount', direction: 'desc' },
  });

  const handleSort = (sortKey: EligibleInvoiceUrlState['sort']['key']) => {
    const updatedSortDirection = urlState.sort?.direction === 'asc' ? 'desc' : 'asc';
    addAction('invoice-central::sort::changed', {
      invoiceType: 'eligible',
      sortKey,
      sortDirection: updatedSortDirection,
      makerOrganizationUuid,
      currency,
    });

    if (urlState.sort && sortKey === urlState.sort.key) {
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: updatedSortDirection,
        },
      }));
    } else {
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: 'desc',
        },
      }));
    }

    reset();
  };

  const handleSearchValue = (search: string | undefined) => {
    addAction('invoice-central::invoices-searched', {
      invoiceType: 'eligible',
      makerOrganizationUuid,
      currency,
    });

    setUrlState((f) => ({
      ...f,
      searchValue: search?.trim(),
    }));
    reset();
  };

  const handleNextPage = () => {
    if (nextCursor) {
      goToNextPage(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    goToPreviousPage();
  };

  const handleFilters = (newFilters?: EligibleInvoiceFilterOptions) => {
    addAction('invoice-central::filters::changed', {
      invoiceType: 'eligible',
      makerOrganizationUuid,
      currency,
      filters: Object.keys(newFilters ?? {}),
    });

    const mergedFilters = mergeFilters({ prevFilters: urlState.filters, newFilters });
    setUrlState((f) => {
      return {
        ...f,
        filters: mergedFilters,
      };
    });
    reset();
  };

  const clearFilters = () => {
    setUrlState((f) => {
      const updatedState: Partial<EligibleInvoiceUrlState> = {
        ...f,
        filters: undefined,
      };

      return updatedState;
    });
    reset();
  };

  return (
    <div>
      <EligibleInvoiceFilters
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        handleFilters={handleFilters}
        currency={currency}
        filters={urlState.filters}
        takerMarkets={takerMarkets}
      />
      <div
        className="flex flex-col justify-between border-y border-deepGray-500/[12%] bg-canvas sm:flex-row"
        data-intercom-target={'invoiceCentralInvoiceControls' satisfies IntercomDataTargetNames}
      >
        {/* Search Bar */}
        <div className="flex flex-col items-center gap-3 p-4 sm:flex-row">
          <SearchInput
            className="w-full sm:w-60"
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => {
              setSearchInput('');
              handleSearchValue(undefined);
            }}
            placeholder={t('invoices.searchInputPlaceholder')}
            value={searchInput}
            withSearchIcon
            onKeyDown={(e) => e.key === 'Enter' && handleSearchValue(searchInput)}
          />
          <Button
            className="w-full bg-canvas sm:w-fit"
            onClick={() => handleSearchValue(searchInput)}
            variant="secondary"
          >
            {t('core.search')}
          </Button>
        </div>
        {/* Filters */}
        <div className="flex items-center gap-2 p-4">
          <div className="relative">
            <Button onClick={() => setFilterOpen(true)} variant="primary">
              {t('core.filters')}
            </Button>
            {Object.keys(urlState.filters ?? {}).length > 0 && (
              <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-lightBlue-500 text-xs font-medium text-white">
                {Object.keys(urlState.filters ?? {}).length}
              </div>
            )}
          </div>
          <Button
            disabled={Object.keys(urlState.filters ?? {}).length === 0}
            onClick={clearFilters}
            variant="secondary"
          >
            {t('core.clearFilters')}
          </Button>
        </div>
      </div>
      <QueryBoundaries
        LoadingComponent={() => (
          <Table>
            <TableBody>
              <TableDisplaySkeletonRows colSpan={7} rowCount={10} />
            </TableBody>
          </Table>
        )}
        ErrorComponent={() => (
          <div className="px-4 pb-6 pt-8">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
      >
        <EligibleInvoicesTable
          currentCursor={currentCursor}
          setNextCursor={setNextCursor}
          urlState={urlState as EligibleInvoiceUrlState}
          handleSort={handleSort}
        />
      </QueryBoundaries>
      <TablePagination
        className="p-6"
        onNextChange={handleNextPage}
        onPrevChange={handlePreviousPage}
        onPageSizeChange={(e) => setUrlState((f) => ({ ...f, pageSize: Number(e.target.value) }))}
        pageSize={urlState.pageSize ?? 25}
        disablePrev={!canGoBack}
        disableNext={!nextCursor}
        currentPage={currentPage}
      />
    </div>
  );
};

const EligibleInvoices = () => {
  const {
    urlParams: { makerOrganizationUuid, currency },
    buyer: { takerMarkets },
  } = useInvoiceCentralLayoutContext();

  return (
    <EligibleInvoicesContent
      key={`${makerOrganizationUuid}-${currency}`}
      makerOrganizationUuid={makerOrganizationUuid}
      currency={currency}
      takerMarkets={takerMarkets}
    />
  );
};

export default EligibleInvoices;
